import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Visual Sweeps — vue React staircase + debrief (cyanotype Wave M2-game-chrome).
 *
 * Lit le state via `adapter.subscribe` (`useSyncExternalStore`).
 * Dispatche les inputs user via `adapter.handleDirectionPick`.
 *
 * Pour le debrief, lit `snapshot.staircase` via `useGameShellFromContext` et
 * compose le rendu via `<DebriefShell>`.
 */
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DebriefShell, useActionBar, useGameShellFromContext } from '@labrain/game-shell';
import { DIRECTIONS, DIRECTION_LABELS, staircaseConfig, } from './params';
const FIELD_SIZE = 280;
const DOT_RADIUS = 2;
const DOT_SPEED = 1.4;
function directionVector(d) {
    if (d === 'up')
        return { vx: 0, vy: -DOT_SPEED };
    if (d === 'down')
        return { vx: 0, vy: DOT_SPEED };
    if (d === 'left')
        return { vx: -DOT_SPEED, vy: 0 };
    return { vx: DOT_SPEED, vy: 0 };
}
function makeDots(stim) {
    const dots = [];
    const { vx, vy } = directionVector(stim.direction);
    for (let i = 0; i < stim.nDots; i += 1) {
        const coherent = Math.random() < stim.coherence;
        if (coherent) {
            dots.push({
                x: Math.random() * FIELD_SIZE,
                y: Math.random() * FIELD_SIZE,
                vx,
                vy,
                coherent: true,
            });
        }
        else {
            const angle = Math.random() * Math.PI * 2;
            dots.push({
                x: Math.random() * FIELD_SIZE,
                y: Math.random() * FIELD_SIZE,
                vx: Math.cos(angle) * DOT_SPEED,
                vy: Math.sin(angle) * DOT_SPEED,
                coherent: false,
            });
        }
    }
    return dots;
}
function DotsRenderer({ stim, visible }) {
    const canvasRef = useRef(null);
    const rafRef = useRef(null);
    useEffect(() => {
        if (!visible)
            return;
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        const dots = makeDots(stim);
        let running = true;
        const draw = () => {
            if (!running)
                return;
            ctx.clearRect(0, 0, FIELD_SIZE, FIELD_SIZE);
            ctx.fillStyle = '#1a1a1a';
            ctx.fillRect(0, 0, FIELD_SIZE, FIELD_SIZE);
            ctx.fillStyle = '#f5f1e8';
            for (const d of dots) {
                d.x += d.vx;
                d.y += d.vy;
                if (d.x < 0)
                    d.x += FIELD_SIZE;
                else if (d.x > FIELD_SIZE)
                    d.x -= FIELD_SIZE;
                if (d.y < 0)
                    d.y += FIELD_SIZE;
                else if (d.y > FIELD_SIZE)
                    d.y -= FIELD_SIZE;
                ctx.beginPath();
                ctx.arc(d.x, d.y, DOT_RADIUS, 0, Math.PI * 2);
                ctx.fill();
            }
            rafRef.current = requestAnimationFrame(draw);
        };
        rafRef.current = requestAnimationFrame(draw);
        return () => {
            running = false;
            if (rafRef.current !== null)
                cancelAnimationFrame(rafRef.current);
        };
    }, [stim, visible]);
    return (_jsx("canvas", { ref: canvasRef, width: FIELD_SIZE, height: FIELD_SIZE, className: [
            'transition-opacity duration-feedback',
            visible ? 'opacity-100' : 'opacity-0',
        ].join(' ') }));
}
function MaskRenderer({ visible }) {
    if (!visible)
        return null;
    return (_jsx("div", { className: "grid grid-cols-12 grid-rows-12 gap-[2px] p-s2", style: { width: FIELD_SIZE, height: FIELD_SIZE, background: '#1a1a1a' }, children: Array.from({ length: 144 }).map((_, i) => (_jsx("div", { className: "rounded-full", style: { background: i % 2 === 0 ? '#f5f1e8' : '#1a1a1a' }, "aria-hidden": true }, i))) }));
}
function FixationCross() {
    return (_jsx("div", { className: "flex items-center justify-center bg-cyan-network", style: { width: FIELD_SIZE, height: FIELD_SIZE }, children: _jsx("span", { className: "select-none font-display text-[44px] text-cyan-surface", children: "+" }) }));
}
function DirectionPicker({ onPick, disabled }) {
    const { t } = useTranslation('games');
    return (_jsxs("div", { className: "relative grid items-center justify-items-center", style: {
            width: FIELD_SIZE,
            height: FIELD_SIZE,
            gridTemplateColumns: '1fr 1fr 1fr',
            gridTemplateRows: '1fr 1fr 1fr',
        }, children: [_jsx("div", {}), _jsx("button", { type: "button", disabled: disabled, onClick: () => onPick('up'), "aria-label": DIRECTION_LABELS.up, className: "flex h-[60px] w-[60px] items-center justify-center rounded-full border border-cyan-border bg-cyan-surface text-[28px] text-cyan-text transition duration-feedback ease-soft-wind hover:scale-110 hover:bg-cyan-bg active:scale-95 disabled:cursor-not-allowed disabled:opacity-50", children: DIRECTION_LABELS.up }), _jsx("div", {}), _jsx("button", { type: "button", disabled: disabled, onClick: () => onPick('left'), "aria-label": DIRECTION_LABELS.left, className: "flex h-[60px] w-[60px] items-center justify-center rounded-full border border-cyan-border bg-cyan-surface text-[28px] text-cyan-text transition duration-feedback ease-soft-wind hover:scale-110 hover:bg-cyan-bg active:scale-95 disabled:cursor-not-allowed disabled:opacity-50", children: DIRECTION_LABELS.left }), _jsx("span", { className: "font-display text-[14px] text-cyan-text-secondary", children: t('visual_sweeps.question') }), _jsx("button", { type: "button", disabled: disabled, onClick: () => onPick('right'), "aria-label": DIRECTION_LABELS.right, className: "flex h-[60px] w-[60px] items-center justify-center rounded-full border border-cyan-border bg-cyan-surface text-[28px] text-cyan-text transition duration-feedback ease-soft-wind hover:scale-110 hover:bg-cyan-bg active:scale-95 disabled:cursor-not-allowed disabled:opacity-50", children: DIRECTION_LABELS.right }), _jsx("div", {}), _jsx("button", { type: "button", disabled: disabled, onClick: () => onPick('down'), "aria-label": DIRECTION_LABELS.down, className: "flex h-[60px] w-[60px] items-center justify-center rounded-full border border-cyan-border bg-cyan-surface text-[28px] text-cyan-text transition duration-feedback ease-soft-wind hover:scale-110 hover:bg-cyan-bg active:scale-95 disabled:cursor-not-allowed disabled:opacity-50", children: DIRECTION_LABELS.down }), _jsx("div", {})] }));
}
export function VSStaircaseView({ adapter, level: _level, onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const subscribe = useCallback((notify) => adapter.subscribe(() => notify()), [adapter]);
    const getSnapshot = useCallback(() => adapter.getState(), [adapter]);
    const adapterState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const shell = useGameShellFromContext();
    const handlePick = useCallback((d) => adapter.handleDirectionPick(d), [adapter]);
    const trialDisplay = shell.snapshot.staircase.trialIdx + 1;
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s5 py-s4", children: [_jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsx("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('common:labels.trial_count', {
                            current: Math.min(trialDisplay, staircaseConfig.maxTrials),
                            total: staircaseConfig.maxTrials,
                        }) }), _jsx("button", { type: "button", onClick: () => {
                            shell.abort('user');
                            onAbort();
                        }, className: "border border-cyan-border-faint px-s3 py-s1 text-[11px] text-cyan-text-secondary hover:border-cyan-border", children: t('common:actions.stop') })] }), _jsxs("div", { className: "relative", children: [adapterState.subPhase === 'fixation' && _jsx(FixationCross, {}), adapterState.subPhase === 'stimulus' && adapterState.stim && (_jsx(DotsRenderer, { stim: adapterState.stim, visible: true })), adapterState.subPhase === 'mask' && _jsx(MaskRenderer, { visible: true }), adapterState.subPhase === 'awaiting_response' && (_jsx(DirectionPicker, { onPick: handlePick, disabled: false })), adapterState.subPhase === 'feedback' && (_jsxs("div", { className: "flex flex-col items-center justify-center gap-s2 bg-cyan-network", style: { width: FIELD_SIZE, height: FIELD_SIZE }, children: [_jsx("p", { className: [
                                    'font-display text-[40px]',
                                    adapterState.feedback === 'correct' ? 'text-cyan-core' : 'text-cyan-media',
                                ].join(' '), children: adapterState.feedback === 'correct' ? '✓' : '✗' }), _jsx("p", { className: "px-s4 text-center text-[12px] text-cyan-surface", children: adapterState.feedback === 'correct'
                                    ? t('visual_sweeps.feedback.correct')
                                    : t('visual_sweeps.feedback.incorrect') })] })), (adapterState.subPhase === 'iti' || adapterState.subPhase === 'idle') && (_jsx("div", { className: "bg-cyan-network", style: { width: FIELD_SIZE, height: FIELD_SIZE }, "aria-hidden": true }))] }), _jsxs("div", { className: "text-[11px] text-cyan-text-secondary", children: [t('common:labels.current_flash'), " : ", _jsxs("span", { className: "font-mono", children: [adapterState.stim?.durationMs ?? '—', " ms"] }), ' · ', t('common:labels.coherence'), " :", ' ', _jsx("span", { className: "font-mono", children: t('visual_sweeps.coherence_pct', { value: Math.round(adapterState.variant.coherence * 100) }) })] })] }));
}
function ageEquivalentKey(thresholdMs) {
    if (thresholdMs === null)
        return '';
    if (thresholdMs <= 50)
        return 'common:age.elite_active_25';
    if (thresholdMs <= 100)
        return 'common:age.active_35';
    if (thresholdMs <= 180)
        return 'common:age.around_45';
    if (thresholdMs <= 280)
        return 'common:age.around_55';
    return 'common:age.around_65_useful';
}
function recommendKey(threshold) {
    if (threshold !== null && threshold <= 100) {
        return 'visual_sweeps.debrief.recommend_excellent';
    }
    if (threshold !== null && threshold <= 200) {
        return 'visual_sweeps.debrief.recommend_good';
    }
    return 'visual_sweeps.debrief.recommend_keep';
}
const ALL_DIRECTIONS = DIRECTIONS;
export function VSDebriefView({ level: _level, onAbort: _onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const shell = useGameShellFromContext();
    const staircase = shell.snapshot.staircase;
    const threshold = staircase.thresholdEstimate;
    const trialsCompleted = staircase.trialIdx;
    const trialsCorrect = staircase.trialLog.filter((tt) => tt.response.correct).length;
    const accuracy = trialsCompleted === 0
        ? 0
        : Math.round((trialsCorrect / trialsCompleted) * 100);
    const bestDuration = useMemo(() => {
        if (staircase.trialLog.length === 0)
            return staircaseConfig.startValue;
        return Math.min(...staircase.trialLog.map((tt) => tt.value));
    }, [staircase]);
    const fallback = !!staircase.fallback;
    const thresholdLabel = threshold === null ? '—' : `${Math.round(threshold)} ms`;
    // Anti-warning useless variable (stat 4-AFC chance level).
    void ALL_DIRECTIONS;
    const replay = () => {
        window.location.reload();
    };
    // Sprint 2.2 L3 : CTA debrief poussées dans la sticky bottom ActionBar.
    // ctaReplay + ctaBack restent inline dans <DebriefShell> par sécurité visuelle
    // (utiles si ActionBarProvider absent, ex tests unitaires de DebriefShell).
    useActionBar({
        primary: (_jsx("button", { type: "button", onClick: replay, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.replay') })),
        secondary: (_jsx(Link, { to: "/library", className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.choose_another') })),
    });
    const ageKey = ageEquivalentKey(threshold);
    const equivalentAge = ageKey ? t('common:age.equivalent_template', { label: t(ageKey) }) : '—';
    return (_jsx(DebriefShell, { eyebrow: t('common:eyebrow.session_done'), title: t('visual_sweeps.debrief.title'), scoreLabel: t('visual_sweeps.debrief.score_label'), scoreValue: thresholdLabel, equivalentAge: equivalentAge, fallbackBadge: fallback, metrics: [
            { label: t('common:metrics.trials'), value: trialsCompleted },
            { label: t('common:metrics.correct_pct'), value: `${accuracy}%` },
            { label: t('common:metrics.shortest'), value: `${Math.round(bestDuration)} ms` },
        ], recommendation: t(recommendKey(threshold)), scientificCaveat: t('visual_sweeps.debrief.scientific_caveat') }));
}
//# sourceMappingURL=staircaseView.js.map